const ALLOWED_ZIPS = new Set(['34741', '34744', '34746', '34747', '34758', '34759']);
const LOCAL_DELIVERY_STORAGE_KEY = 'cart-local-delivery-state';
const LOCAL_DELIVERY_SYNC_EVENT = 'cart-local-delivery:sync';
let localDeliveryInstanceCount = 0;

function formatToday() {
  const date = new Date();
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function readStoredState() {
  try {
    const raw = window.sessionStorage.getItem(LOCAL_DELIVERY_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeStoredState(root) {
  try {
    const toggle = root.querySelector('[data-local-delivery-toggle]');
    const zipInput = root.querySelector('[data-local-delivery-zip]');
    const dateInput = root.querySelector('[data-local-delivery-date]');

    window.sessionStorage.setItem(
      LOCAL_DELIVERY_STORAGE_KEY,
      JSON.stringify({
        checked: Boolean(toggle?.checked),
        zip: zipInput?.value || '',
        date: dateInput?.value || '',
      })
    );
  } catch {
    // Ignore storage failures and fall back to cart attributes only.
  }
}

function updateCartAttributes(root) {
  if (!window.Theme?.routes?.cart_update_url) return;

  const toggle = root.querySelector('[data-local-delivery-toggle]');
  const zipInput = root.querySelector('[data-local-delivery-zip]');
  const dateInput = root.querySelector('[data-local-delivery-date]');

  const attributes = {
    'Local Delivery': toggle?.checked ? 'Yes' : 'No',
    'Delivery ZIP': toggle?.checked ? zipInput?.value || '' : '',
    'Delivery Date': toggle?.checked ? dateInput?.value || '' : '',
  };

  window.clearTimeout(root.localDeliverySaveTimer);
  root.localDeliverySaveTimer = window.setTimeout(() => {
    fetch(`${Theme.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ attributes }),
    }).catch(() => {});
  }, 250);
}

function initLocalDelivery(root) {
  if (!(root instanceof HTMLElement) || root.dataset.localDeliveryBound === 'true') return;

  root.dataset.localDeliveryBound = 'true';
  localDeliveryInstanceCount += 1;
  root.dataset.localDeliveryInstanceId = String(localDeliveryInstanceCount);

  const toggle = root.querySelector('[data-local-delivery-toggle]');
  const fallback = root.querySelector('[data-local-delivery-fallback]');
  const zipInput = root.querySelector('[data-local-delivery-zip]');
  const zipFallback = root.querySelector('[data-local-delivery-zip-fallback]');
  const dateInput = root.querySelector('[data-local-delivery-date]');
  const dateFallback = root.querySelector('[data-local-delivery-date-fallback]');
  const fields = root.querySelector('[data-local-delivery-fields]');
  const zipError = root.querySelector('[data-local-delivery-zip-error]');
  const dateError = root.querySelector('[data-local-delivery-date-error]');
  const ctas = root.parentElement?.querySelector('.cart__ctas');
  const primaryButtons = ctas ? Array.from(ctas.querySelectorAll('[data-local-delivery-checkout]')) : [];
  const acceleratedTargets = ctas ? Array.from(ctas.querySelectorAll('[data-local-delivery-accelerated]')) : [];

  if (!toggle || !fallback || !zipInput || !zipFallback || !dateInput || !dateFallback || !fields) return;

  const applyStoredState = (state) => {
    if (!state) return;

    toggle.checked = Boolean(state.checked);
    if (typeof state.zip === 'string') zipInput.value = state.zip;
    if (typeof state.date === 'string') dateInput.value = state.date;
  };

  applyStoredState(readStoredState());

  if (dateInput.type === 'date') {
    dateInput.min = formatToday();
  }

  primaryButtons.forEach((button) => {
    if (!button.dataset.localDeliveryInitialDisabled) {
      button.dataset.localDeliveryInitialDisabled = button.disabled ? 'true' : 'false';
    }
  });

  const sanitizeZip = () => {
    zipInput.value = zipInput.value.replace(/\D/g, '').slice(0, 5);
  };

  const getZipMessage = () => {
    if (!zipInput.value) return 'Enter a valid local ZIP code.';
    if (!ALLOWED_ZIPS.has(zipInput.value)) return 'We only deliver locally to 34741, 34744, 34746, 34747, 34758, and 34759.';
    return '';
  };

  const getDateMessage = () => {
    if (!dateInput.value) return 'Select a delivery date.';
    return '';
  };

  const updateButtonState = (isValid) => {
    const shouldDisable = toggle.checked && !isValid;

    primaryButtons.forEach((button) => {
      const initiallyDisabled = button.dataset.localDeliveryInitialDisabled === 'true';
      button.disabled = initiallyDisabled || shouldDisable;
      button.setAttribute('aria-disabled', button.disabled ? 'true' : 'false');
    });

    acceleratedTargets.forEach((target) => {
      if (shouldDisable) {
        target.setAttribute('aria-disabled', 'true');
        target.setAttribute('inert', '');
        target.classList.add('cart-local-delivery-disabled');
      } else {
        target.removeAttribute('aria-disabled');
        target.removeAttribute('inert');
        target.classList.remove('cart-local-delivery-disabled');
      }
    });
  };

  const syncFieldState = () => {
    const checked = toggle.checked;

    fields.hidden = !checked;
    fields.setAttribute('aria-hidden', checked ? 'false' : 'true');
    toggle.setAttribute('aria-expanded', checked ? 'true' : 'false');
    fallback.disabled = checked;
    zipInput.disabled = !checked;
    dateInput.disabled = !checked;
    zipFallback.disabled = checked;
    dateFallback.disabled = checked;

    if (!checked) {
      zipFallback.value = '';
      dateFallback.value = '';
      zipError.hidden = true;
      dateError.hidden = true;
    }
  };

  const validate = () => {
    const checked = toggle.checked;

    if (!checked) {
      updateButtonState(true);
      return true;
    }

    const zipMessage = getZipMessage();
    const dateMessage = getDateMessage();
    const isValid = !zipMessage && !dateMessage;

    zipError.textContent = zipMessage;
    dateError.textContent = dateMessage;
    zipError.hidden = !zipMessage;
    dateError.hidden = !dateMessage;

    updateButtonState(isValid);
    return isValid;
  };

  const refresh = ({ persist = true, broadcast = true } = {}) => {
    syncFieldState();
    validate();
    writeStoredState(root);
    if (persist) updateCartAttributes(root);

    if (broadcast) {
      window.dispatchEvent(
        new CustomEvent(LOCAL_DELIVERY_SYNC_EVENT, {
          detail: { sourceId: root.dataset.localDeliveryInstanceId },
        })
      );
    }
  };

  sanitizeZip();
  refresh({ persist: false, broadcast: false });

  toggle.addEventListener('change', () => refresh());
  zipInput.addEventListener('input', () => {
    sanitizeZip();
    refresh();
  });
  dateInput.addEventListener('input', () => refresh());
  dateInput.addEventListener('change', () => refresh());
  window.addEventListener(LOCAL_DELIVERY_SYNC_EVENT, (event) => {
    if (event.detail?.sourceId === root.dataset.localDeliveryInstanceId) return;

    applyStoredState(readStoredState());
    sanitizeZip();
    refresh({ persist: false, broadcast: false });
  });
}

function initAllLocalDelivery() {
  document.querySelectorAll('[data-local-delivery-root]').forEach(initLocalDelivery);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllLocalDelivery);
} else {
  initAllLocalDelivery();
}

const localDeliveryObserver = new MutationObserver(() => {
  initAllLocalDelivery();
});

localDeliveryObserver.observe(document.documentElement, {
  childList: true,
  subtree: true,
});
